import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { fleet, type AssetControlView, type FleetCommandRow } from "@/api/client";
import { PageHead } from "@/components/PageHead";
import { Select, SearchInput } from "@/components/inputs";
import { Icon } from "@/components/icons";
import { CAP, useSession } from "@/auth/session";
import { POLL, ago } from "@/lib/format";

/** Asset Control — send a command to one asset and see what happened to it.
 *
 *  THE PAGE'S ONE JOB IS TO NOT LIE ABOUT STATE, because three different
 *  things can look like "immobilised" and only one of them means the vehicle
 *  cannot move:
 *
 *    HOLD PLACED, NOT YET APPLIED  the device has the command and is waiting
 *                                  for the vehicle to halt. It is STILL MOVING
 *                                  and still mobile. Rendering this as
 *                                  "immobilised" is the error an operator would
 *                                  act on.
 *    APPLIED                       in force on the vehicle.
 *    HOLD PLACED, NOT ACTUATABLE   no connected part is installed, so the lien
 *                                  stands and nothing can reach the asset.
 *
 *  And the EFFECTIVE TYPE is shown with the level that decided it, because the
 *  highest open level dictates the type (OFL-06k): a company that asked for
 *  WHEELLOCK can be getting FREEWHEEL while a financier hold stands, and a bare
 *  "immobilised" would hide that from both of them. */

const ACT = { MOBILIZE: 1, FREEWHEEL: 2, WHEELLOCK: 3 } as const;
const ACT_LABEL: Record<number, string> = {
  1: "Mobilised", 2: "Freewheel (throttle cut)", 3: "Wheel lock",
};
/** Orbit's actor → level map. Labels only; the server derives the level from
 *  the caller's role and refuses one sent in a body. */
const LEVEL_LABEL: Record<number, string> = {
  2: "Fleet manager", 3: "Company", 5: "Financier", 8: "Aphelion (platform)",
};
/** `RS_*` from the firmware header, for the command log. Mapped, never
 *  invented — `tests/fleet` checks Orbit's copy against that file. */
const REASON: Record<number, string> = {
  0: "OK", 1: "Interlock", 2: "Auth failed", 3: "Unsupported / malformed",
  4: "Device busy (booting)", 5: "Superseded", 6: "Already immobilised",
  7: "Replay (stale seq)", 8: "Link not secure", 9: "Expired",
  10: "No rights", 11: "No grant", 12: "Clock untrusted",
};
const STATE_COLOR: Record<string, string> = {
  pending: "var(--t-mute)", sent: "var(--info)", received: "var(--warn)",
  applied: "var(--ok)", nacked: "var(--bad)", superseded: "var(--t-dim)",
  expired_undelivered: "var(--bad)", expired_unapplied: "var(--bad)",
  cancelled: "var(--t-dim)",
};
/** What each state MEANS for the vehicle — the column an operator actually
 *  needs, and the one a bare state name does not give them. */
const STATE_MEANS: Record<string, string> = {
  pending: "not sent yet",
  sent: "sent, no answer yet",
  received: "device has it — WAITING FOR THE VEHICLE TO HALT",
  applied: "in force on the vehicle",
  nacked: "refused by the device",
  superseded: "replaced by a later command at this level",
  expired_undelivered: "never reached the device",
  expired_unapplied: "device had it and gave up",
  cancelled: "cancelled",
};

function StateChip({ c }: { c: FleetCommandRow }) {
  const col = STATE_COLOR[c.state] ?? "var(--t-mute)";
  return (
    <span className="chip" title={STATE_MEANS[c.state] ?? c.state}
      style={{ color: col, background: `color-mix(in srgb, ${col} 13%, transparent)` }}>
      {c.state}
    </span>
  );
}

function Banner({ v }: { v: AssetControlView }) {
  const armed = v.commands.find((c) => c.state === "received");
  if (!v.immobile) {
    return (
      <div className="panel panel-pad" style={{ borderLeft: "3px solid var(--ok)" }}>
        <b style={{ color: "var(--ok)" }}>MOBILE</b> — no holds on this asset.
      </div>
    );
  }
  // IMMOBILE per Orbit's holds. Now say which of the three it actually is.
  const applied = v.commands.some((c) => c.state === "applied");
  const colour = applied ? "var(--bad)" : "var(--warn)";
  return (
    <div className="panel panel-pad" style={{ borderLeft: `3px solid ${colour}` }}>
      <b style={{ color: colour }}>
        {applied ? "IMMOBILISED" : armed ? "ARMED — NOT YET APPLIED" : "HOLD PLACED"}
      </b>
      {" — "}
      {ACT_LABEL[v.effective_action]}
      {v.effective_level != null && (
        <> , decided by <b>{LEVEL_LABEL[v.effective_level] ?? `level ${v.effective_level}`}</b></>
      )}
      {!applied && armed && (
        <div style={{ marginTop: 6, fontSize: 12.5 }}>
          The device has the command and is waiting for the vehicle to come to a
          halt (controller data valid, speed and RPM zero, for 5 s).
          <b> The vehicle can still move until then.</b>
        </div>
      )}
      {!v.actuatable && (
        <div style={{ marginTop: 6, fontSize: 12.5, color: "var(--warn)" }}>
          No connected part is installed on this asset, so the hold is recorded
          but nothing can be sent to it.
        </div>
      )}
    </div>
  );
}

export function AssetDataControlPage() {
  const [params, setParams] = useSearchParams();
  const [assetId, setAssetId] = useState("");
  const [action, setAction] = useState<number>(ACT.FREEWHEEL);
  const [note, setNote] = useState("");
  const { can } = useSession();
  const qc = useQueryClient();
  const mayCommand = can(CAP.IMMOBILIZE);

  // `?asset=` is how the fleet map hands an asset over, the same shape the
  // Window and History pages use for `?imei=`.
  const deep = params.get("asset");
  useEffect(() => {
    if (!deep) return;
    setAssetId(deep);
    setParams((prev) => { const p = new URLSearchParams(prev); p.delete("asset"); return p; },
              { replace: true });
  }, [deep]);

  const q = useQuery({
    queryKey: ["fleet", "asset", assetId],
    queryFn: () => fleet.asset(assetId),
    enabled: assetId.trim().length > 0,
    refetchInterval: POLL.STATE,
  });

  const act = useMutation({
    mutationFn: (what: "immobilize" | "mobilize") =>
      what === "immobilize"
        ? fleet.immobilize(assetId, { action, note: note || undefined })
        : fleet.mobilize(assetId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fleet", "asset", assetId] }),
  });

  const v = q.data;

  return (
    <div className="mpage on">
      <PageHead eyebrow="Asset Data · Asset Control" title="Send a command"
        desc={<>Immobilise or mobilise one asset, and see exactly what the device
          did with the command. A command is not in force until it says
          <b> applied</b>.</>} />

      <div className="panel panel-pad" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <label className="simlab">Asset (VIN / BIN)</label>
            <SearchInput mono style={{ width: 260 }} placeholder="DRILL-ASSET-001"
              value={assetId} onChange={(e) => setAssetId(e.target.value)} />
          </div>
          <div>
            <label className="simlab">Immobilise as</label>
            <Select style={{ width: 210 }} value={action}
              onChange={(e) => setAction(Number(e.target.value))}>
              <option value={ACT.FREEWHEEL}>Freewheel (throttle cut)</option>
              <option value={ACT.WHEELLOCK}>Wheel lock</option>
            </Select>
          </div>
          <div>
            <label className="simlab">Note (optional)</label>
            <SearchInput style={{ width: 220 }} placeholder="why"
              value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>

        {/* The write controls sit behind CAP.IMMOBILIZE, not behind the module
            gate: opening Fleet is a read, stopping somebody's vehicle is not. */}
        <div style={{ display: "flex", gap: 10, alignItems: "center",
                      justifyContent: "flex-end", marginTop: 14, flexWrap: "wrap" }}>
          {!mayCommand && (
            <span style={{ fontSize: 11.5, color: "var(--t-mute)", marginRight: "auto" }}>
              You can view an asset's state. Sending a command needs the
              immobilise permission.
            </span>
          )}
          {act.isError && (
            <span style={{ fontSize: 11.5, color: "var(--bad)", marginRight: "auto" }}>
              {String((act.error as Error)?.message ?? "command failed")}
            </span>
          )}
          <button className="btn ghost" disabled={!assetId || !mayCommand || act.isPending}
            onClick={() => act.mutate("mobilize")}>
            <Icon name="ok" /> Mobilise
          </button>
          <button className="btn red" disabled={!assetId || !mayCommand || act.isPending}
            onClick={() => act.mutate("immobilize")}>
            <Icon name="blocked" /> Immobilise
          </button>
        </div>
      </div>

      {!assetId && (
        <div className="panel panel-pad cap">
          Enter an asset, or click an asset on the Overview map and choose Asset
          Control.
        </div>
      )}
      {assetId && q.isError && (
        <div className="panel panel-pad" style={{ borderLeft: "3px solid var(--bad)" }}>
          No such asset, or it is not yours.
        </div>
      )}

      {v && (
        <>
          <Banner v={v} />

          <div className="section-label tight">
            Holds — every one must clear before the asset is mobile
          </div>
          <div className="tblwrap">
            <table className="tbl">
              <thead><tr>
                <th>Level</th><th>Type</th><th>Placed by</th><th>When</th><th>Note</th>
              </tr></thead>
              <tbody>
                {v.holds.length === 0 && (
                  <tr><td colSpan={5} className="cap">No holds.</td></tr>
                )}
                {v.holds.map((h) => (
                  <tr key={h.level}>
                    <td>{LEVEL_LABEL[h.level] ?? `level ${h.level}`}</td>
                    <td>{ACT_LABEL[h.action]}</td>
                    <td className="mono">{h.placed_by}</td>
                    <td>{h.placed_at ? `${ago(h.placed_at)} ago` : "—"}</td>
                    <td>{h.note ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="section-label tight">Command log</div>
          <div className="tblwrap">
            <table className="tbl">
              <thead><tr>
                <th>When</th><th>Level</th><th>Command</th><th>State</th>
                <th>Means</th><th>Reason</th><th>By</th>
              </tr></thead>
              <tbody>
                {v.commands.length === 0 && (
                  <tr><td colSpan={7} className="cap">Nothing sent yet.</td></tr>
                )}
                {v.commands.map((c) => (
                  <tr key={c.cmd_id}>
                    <td>{c.created_at ? `${ago(c.created_at)} ago` : "—"}</td>
                    <td>{c.level != null ? (LEVEL_LABEL[c.level] ?? c.level) : "—"}</td>
                    <td>{ACT_LABEL[c.action] ?? c.action}</td>
                    <td><StateChip c={c} /></td>
                    <td style={{ fontSize: 12 }}>{STATE_MEANS[c.state] ?? "—"}</td>
                    <td>{c.reason_code != null && c.reason_code !== 0
                      ? (REASON[c.reason_code] ?? `rs ${c.reason_code}`) : "—"}</td>
                    <td className="mono" style={{ fontSize: 11 }}>{c.created_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
